import { create } from 'zustand';
import { API_BASE } from '../config';
import { useCityStore } from './cityStore';
import { useCutscene, cutsceneBridge } from './cutscene';
import { stateAt, setGeo, spotPos } from './cityMotion';
import { planShot, chooseSide, shotAt, setSightTest } from './sceneShots';
import { useWorldStore } from '../state/worldStore';

// A MOMENT, RE-STAGED FOR ONE STILL (2026-11-01).
//
//   world.hypetunes.com/?moment=<conversation id>&mt=<pass>&time=<hour>&weather=clear&select=0&dpr=1&cars=0
//
// Khari: "Artists' auto-posts should be derived from their actual in-world
// events … a 'directed' 3D screenshot from the scene in the world." render-bot
// opens this (its built-in world-still-v1) in GPU Chrome: two residents who
// talked, at the pair of spots they talked on, at the place, under that hour's
// sky — staged from the API's record of it (GET /api/world/city/moments/:id),
// exactly as a City Stories replay stages an episode (city/replay): a city of
// just its cast, the live city set aside. The cutscene director's two-shot
// frames them (sceneShots planShot 'two'), held still; nothing else is on
// screen (the cutscene phase hides every label and control).
//
// When it is framed and both bodies are drawn, `window.__shotReady = true` and
// render-bot takes the picture. When it cannot be (no such moment, no body for
// someone), `window.__shotError` says why, so the job fails at once rather than
// timing out. Nobody ever sees this page.

const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
export const MOMENT_ID = qs && /^\d+$/.test(qs.get('moment') || '') ? Number(qs.get('moment')) : null;
const MOMENT_PASS = qs ? qs.get('mt') : null;

// From the first frame: the walk, the room chase and the clamps stand down.
if (MOMENT_ID) cutsceneBridge.active = true;

/** What the in-canvas director last saw (MomentDirector writes it; MomentBoot's watchdog reads it). */
export const momentBridge = { checks: {}, steady: 0, framed: false };

export const useMoment = create(() => ({
  state: MOMENT_ID ? 'loading' : 'off',   // off | loading | ready | staged | shot | error
  error: null,
  moment: null,
  places: null,
}));

export function momentFail(why) {
  if (typeof window !== 'undefined' && !window.__shotError) window.__shotError = String(why || 'failed');
  useMoment.setState({ state: 'error', error: String(why || 'failed') });
  console.warn(`[moment] ${why}`);
}

export async function loadMoment() {
  if (!MOMENT_ID) return;
  try {
    const res = await fetch(`${API_BASE}/api/world/city/moments/${MOMENT_ID}${MOMENT_PASS ? `?mt=${encodeURIComponent(MOMENT_PASS)}` : ''}`, { cache: 'no-store' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.success) throw new Error(d.error || `could not load the moment (${res.status})`);
    useMoment.setState({ state: 'ready', moment: d.moment, places: d.places || null, error: null });
  } catch (err) {
    momentFail(err.message);
  }
}

// CLOSER, AND OPEN TO THE LENS (2026-11-01). Their pair of spots is where two
// people stand to talk in the live city — at The Court about 1.4 units (3 m)
// apart, so the first real still had them small at either edge with a lamp post
// between. A still stands them at talking distance midway between the spots,
// each turned a little from the other toward the camera's side of the line (the
// side the two-shot will choose: the same test, the lead first), so both read
// three-quarter on rather than in profile.
const GAP = 0.62;     // ≈1.35 m between them
const CHEAT = 0.42;   // ≈24° each, toward the lens

/** The pair's marks for a still: { a: { xz, hd }, b: { xz, hd } }, or null to keep their spots. */
function closeMarks(m, pair) {
  // UP IN A HELICOPTER (API HeliRideService, origin 'heli', 2026-11-05): the API sends where each
  // stands, the pilot on their mark by the pad they flew from and the artist in front of them
  // (`marks`, API city/cityHome); the still is the two of them by the helicopter, brought close the same way.
  const given = (id) => (Array.isArray(m.marks?.[id]) ? [Number(m.marks[id][0]), Number(m.marks[id][1])] : null);
  const A0 = given(m.a.id) || spotPos(m.place, pair[0]); const B0 = given(m.b.id) || spotPos(m.place, pair[1]);
  const D = Math.hypot(B0[0] - A0[0], B0[1] - A0[1]);
  if (!(D > 1e-3)) return null;
  const ux = (B0[0] - A0[0]) / D; const uz = (B0[1] - A0[1]) / D;
  const gap = Math.min(D, GAP);
  const mx = (A0[0] + B0[0]) / 2; const mz = (A0[1] + B0[1]) / 2;
  const A = [mx - (ux * gap) / 2, mz - (uz * gap) / 2];
  const B = [mx + (ux * gap) / 2, mz + (uz * gap) / 2];
  const leadIsB = Number(m.lead) === Number(m.b.id);
  const S = leadIsB ? B : A; const L = leadIsB ? A : B;
  const side = chooseSide({ x: S[0], z: S[1] }, { x: L[0], z: L[1] });
  const sl = Math.hypot(L[0] - S[0], L[1] - S[1]) || 1;
  const nx = (-(L[1] - S[1]) / sl) * side; const nz = ((L[0] - S[0]) / sl) * side;   // out to the camera's side (planShot's)
  const face = (from, to) => {
    const fl = Math.hypot(to[0] - from[0], to[1] - from[1]) || 1;
    const fx = (to[0] - from[0]) / fl; const fz = (to[1] - from[1]) / fl;
    return Math.atan2(fx * Math.cos(CHEAT) + nx * Math.sin(CHEAT), fz * Math.cos(CHEAT) + nz * Math.sin(CHEAT));   // rotationY: 0 = +z
  };
  return { a: { xz: A, hd: face(A, B) }, b: { xz: B, hd: face(B, A) } };
}

/** The two of them at their pair of spots — brought to talking distance — held there; the live city set aside. */
export function stageMoment(now) {
  const { moment: m, places } = useMoment.getState();
  if (!m) return;
  const pairs = places?.pairs || [[0, 1], [2, 3]];
  const pair = pairs[m.pair || 0] || pairs[0];
  const anchors = useWorldStore.getState().venueAnchors || null;
  const build = (marks) => {
    const seg = (spot, mk) => [{ k: 'stay', t0: now - 10 * 60000, t1: now + 60 * 60000, m: 'story', p: m.place, s: spot, a: 'talking', ...(mk || {}) }];
    return {
      v: 1, t: now, tz: 'America/Los_Angeles', ...(places || {}),
      residents: [
        { avatarId: Number(m.a.id), name: m.a.name, segs: seg(pair[0], marks?.a) },
        { avatarId: Number(m.b.id), name: m.b.name, segs: seg(pair[1], marks?.b) },
      ],
      convs: [], holds: [], stories: [], shows: [], sign: null,
    };
  };
  // Their spots first (the geometry has to be in to know where those are), then the marks.
  let snap = build(null);
  useCityStore.getState().setSnap(snap, { replay: true });
  setGeo(snap, anchors);
  const marks = closeMarks(m, pair);
  if (marks) {
    snap = build(marks);
    useCityStore.getState().setSnap(snap, { replay: true });
    setGeo(snap, anchors);
    console.log(`[moment] marks: ${marks.a.xz.map((v) => v.toFixed(2))} / ${marks.b.xz.map((v) => v.toFixed(2))}`);
  }
  // The cutscene phase without a target: labels, bubbles and controls hidden, no letterbox, no cue.
  useCutscene.setState({ phase: 'cut', target: null, caption: null });
  useMoment.setState({ state: 'staged' });
}

/**
 * The frame: the cutscene director's two-shot of them, held at its settled end
 * (no handheld drift). { pos, look, fov }, or null until both can be placed.
 * `sight` (city/momentSight): plan with real sight lines — once the scene is in.
 */
export function momentPose(aspect = 0.8, now = Date.now(), sight = null) {
  const { moment: m } = useMoment.getState();
  const cs = useCityStore.getState();
  if (!m) return null;
  const heights = new Map((useWorldStore.getState().npcList || []).map((n) => [Number(n.avatarId), n.height || 0.75]));
  const person = (id) => {
    const r = cs.byId?.[Number(id)];
    const st = r ? stateAt(r, now, cs.convs) : null;
    if (!st || (!st.x && !st.z)) return null;
    return { id, x: st.x, z: st.z, facing: st.heading || 0, head: (st.y || 0) + (heights.get(Number(id)) || 0.75) * 0.92 };
  };
  const A = person(m.a.id); const B = person(m.b.id);
  if (!A || !B) return null;
  // The one whose moment it is (`lead`, the artist the post is for) faces the lens.
  const lead = Number(m.lead) === Number(m.b.id) ? [B, A] : [A, B];
  let plan;
  if (sight) { sight.band = { a: lead[0], b: lead[1] }; setSightTest(sight); }
  try { plan = planShot('two', lead[0], lead[1], chooseSide(lead[0], lead[1]), aspect, 1); } finally { if (sight) { setSightTest(null); sight.band = null; } }
  return { ...shotAt({ ...plan, shake: 0 }, plan.dur, 0), clear: !!plan.at.clear, k: plan.at.k };
}
